import React, { useState, useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { getCaptchaToken, verifyCaptcha } from '../utils/captcha';
import { submitForm } from '../utils/api';
import { toast } from 'sonner';

export default function ConsultationPopup({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    city: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // reCAPTCHA v3 verification
      const token = await getCaptchaToken('consultation');
      const captchaResult = await verifyCaptcha(token, 'consultation');
      if (!captchaResult.success) {
        toast.error('Verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      await submitForm('consultation', formData);
      setSuccess(true);
      setIsSubmitting(false);
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', email: '', phone: '', company: '', city: '', service: '', message: '' });
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to schedule consultation. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-lg rounded-xl shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors z-[101]"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-6 md:p-8">
          {success ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Message Sent Successfully!</h3>
              <p className="text-slate-400">We will connect with you shortly.</p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Schedule a Consultation</h2>
              <p className="text-slate-400 mb-6 font-light">Tell us about your requirements, and our team will get back to you.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input required placeholder="Full Name *" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                  <Input required type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input placeholder="Phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                  <Input placeholder="Company / Startup Name" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input placeholder="City" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                  <Select value={formData.service} onValueChange={(val) => setFormData({ ...formData, service: val })}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="What are you looking for? *" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 text-white z-[150]">
                      <SelectItem value="fundraising">Fund Raising</SelectItem>
                      <SelectItem value="m_and_a">Mergers & Acquisitions</SelectItem>
                      <SelectItem value="business_advisory">Business Advisory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Textarea placeholder="Briefly describe your requirement" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} className="bg-slate-800 border-slate-700 text-white min-h-[100px]" />

                <Button type="submit" disabled={isSubmitting} className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg font-medium">
                  {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
